import { useRef, useState } from 'react'
import { Icone } from '@/components/ui/Icone'
import { RichText } from '@/lib/content/RichText'
import type { RenduOptions } from '@/lib/content/inline'

/**
 * Une question d'ORDONNANCEMENT — glisser pour remettre dans l'ordre.
 *
 * **Elle était rendue comme un choix multiple**, ce qui la rendait
 * impossible : le serveur compare la SÉQUENCE soumise à la séquence attendue
 * (`Grader::ordre`), et une liste de cases cochées ne porte aucun ordre. Le
 * candidat pouvait tout cocher sans jamais avoir juste.
 *
 * **Deux façons de réordonner, et les deux comptent.** Le glisser au doigt est
 * ce qu'on demande ; les flèches haut/bas sont ce qui rend la question
 * jouable au clavier, avec un lecteur d'écran, et sur un écran qui capte mal
 * le geste. Ce n'est pas une béquille : sur un téléphone tenu d'une main, la
 * flèche est souvent plus rapide que le glissement.
 *
 * **Pas de HTML5 drag-and-drop.** Il ne fonctionne pas au tactile — c'est
 * l'essentiel du public. Les événements de pointeur, eux, couvrent la souris,
 * le doigt et le stylet avec le même code.
 */
export function ListeOrdonnable({
  elements,
  options,
  desactivee,
  onChangement,
}: {
  elements: { id: number; content_md: string }[]
  options: RenduOptions
  desactivee: boolean
  /** L'ordre courant, à chaque déplacement : c'est lui qu'on soumettra. */
  onChangement: (ordre: number[]) => void
}) {
  const [ordre, setOrdre] = useState(elements)

  // L'élément qu'on déplace, et les rectangles des lignes au moment où le
  // geste commence : les relire à chaque mouvement ferait recalculer la mise
  // en page soixante fois par seconde.
  const saisi = useRef<number | null>(null)
  const boites = useRef<{ id: number; milieu: number }[]>([])
  const conteneur = useRef<HTMLUListElement>(null)

  const [enCours, setEnCours] = useState<number | null>(null)

  const appliquer = (suivant: typeof elements): void => {
    setOrdre(suivant)
    onChangement(suivant.map((e) => e.id))
  }

  const deplacer = (depuis: number, vers: number): void => {
    if (vers < 0 || vers >= ordre.length || depuis === vers) return

    const suivant = [...ordre]
    const [element] = suivant.splice(depuis, 1)

    if (element === undefined) return

    suivant.splice(vers, 0, element)
    appliquer(suivant)
  }

  const commencer = (id: number) => (event: React.PointerEvent) => {
    if (desactivee) return

    saisi.current = id
    setEnCours(id)

    // Le pointeur est capturé : le glissement survit à la sortie de la ligne,
    // sinon lâcher un peu à côté annulerait le geste au milieu.
    event.currentTarget.setPointerCapture(event.pointerId)

    const lignes = [...(conteneur.current?.children ?? [])]

    boites.current = lignes.map((ligne, rang) => {
      const r = ligne.getBoundingClientRect()

      return { id: ordre[rang]?.id ?? -1, milieu: r.top + r.height / 2 }
    })
  }

  const bouger = (event: React.PointerEvent): void => {
    const id = saisi.current

    if (id === null) return

    const depuis = ordre.findIndex((e) => e.id === id)

    // La cible est la ligne dont le milieu est le plus proche du pointeur.
    let vers = depuis
    let ecart = Number.POSITIVE_INFINITY

    boites.current.forEach((boite, rang) => {
      const distance = Math.abs(boite.milieu - event.clientY)

      if (distance < ecart) {
        ecart = distance
        vers = rang
      }
    })

    if (vers !== depuis) deplacer(depuis, vers)
  }

  const finir = (): void => {
    saisi.current = null
    setEnCours(null)
  }

  return (
    <ul ref={conteneur} className="mt-3 flex flex-col gap-2">
      {ordre.map((element, rang) => (
        <li
          key={element.id}
          onPointerMove={bouger}
          onPointerUp={finir}
          onPointerCancel={finir}
          className="flex items-center gap-2 rounded-2xl border px-3 py-2 transition-shadow"
          style={{
            borderColor: enCours === element.id ? 'var(--accent-fort)' : 'var(--bordure)',
            background: enCours === element.id ? 'var(--surface-2)' : undefined,
            boxShadow: enCours === element.id ? '0 4px 16px rgba(0,0,0,0.18)' : undefined,
            opacity: desactivee ? 0.6 : 1,
          }}
        >
          {/* La poignée porte le geste, et non toute la ligne : le texte doit
              rester sélectionnable, et un glissement déclenché n'importe où
              empêcherait de faire défiler la page au doigt. */}
          <button
            type="button"
            aria-label={`Déplacer « ${element.content_md.slice(0, 40)} »`}
            disabled={desactivee}
            onPointerDown={commencer(element.id)}
            className="shrink-0 cursor-grab touch-none p-1"
            style={{ color: 'var(--texte-2)' }}
          >
            <Icone nom="trois-points" taille={18} />
          </button>

          <span
            className="w-6 shrink-0 text-[15px] font-bold tabular-nums"
            style={{ color: 'var(--texte-2)' }}
          >
            {rang + 1}.
          </span>

          <span className="min-w-0 flex-1 text-[15px]">
            <RichText texte={element.content_md} options={options} />
          </span>

          {/* Les flèches : le même déplacement, sans geste. Elles rendent la
              question jouable au clavier et au lecteur d'écran, où un
              glissement n'existe pas.

              **Elles disent QUOI elles déplacent** — §150. « Monter » tout
              court était le même mot sur chaque ligne : un candidat au lecteur
              d'écran tabulait dans « Monter, Descendre, Monter, Descendre… »
              douze fois sur six lignes, sans jamais savoir laquelle il tenait.
              Une flèche jouable au clavier mais anonyme ne rend pas la question
              jouable — elle la rend devinable. La poignée juste à côté portait
              déjà son contenu ; les flèches le portent comme elle. */}
          <span className="flex shrink-0 flex-col">
            <button
              type="button"
              aria-label={`Monter « ${element.content_md.slice(0, 40)} »`}
              disabled={desactivee || rang === 0}
              onClick={() => { deplacer(rang, rang - 1); }}
              className="px-1 disabled:opacity-30"
            >
              <span className="block rotate-180"><Icone nom="chevron-bas" taille={14} /></span>
            </button>
            <button
              type="button"
              aria-label={`Descendre « ${element.content_md.slice(0, 40)} »`}
              disabled={desactivee || rang === ordre.length - 1}
              onClick={() => { deplacer(rang, rang + 1); }}
              className="px-1 disabled:opacity-30"
            >
              <Icone nom="chevron-bas" taille={14} />
            </button>
          </span>
        </li>
      ))}
    </ul>
  )
}
